import React, { useState, useEffect } from 'react';
import { QrCode, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function QRCodeDialog({ agentId, agentName }) {
  const [open, setOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadQRCode = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/agents/${agentId}/qrcode?size=400`, {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      setQrCodeUrl(url);
    } catch (error) {
      toast.error('Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !qrCodeUrl) {
      loadQRCode();
    }
  }, [open]);

  const downloadQRCode = async () => {
    if (!qrCodeUrl) return;
    
    try {
      const a = document.createElement('a');
      a.href = qrCodeUrl;
      a.download = `${agentName.replace(/\s+/g, '_')}_QRCode.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('QR Code downloaded!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          data-testid={`qrcode-${agentId}`}
        >
          <QrCode className="w-3 h-3 mr-1" />
          QR Code
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>QR Code for {agentName}</DialogTitle>
          <DialogDescription>
            Scan this QR code to open the public chat directly
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-center bg-white p-6 rounded-xl border-2 border-slate-200">
            {loading ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : qrCodeUrl ? (
              <img
                src={qrCodeUrl}
                alt={`QR Code for ${agentName}`}
                className="w-full max-w-xs"
                data-testid="qrcode-image"
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center text-muted-foreground">
                Failed to load QR code
              </div>
            )}
          </div>

          <div className="flex items-center justify-center space-x-2">
            <Button
              onClick={downloadQRCode}
              className="rounded-full"
              disabled={!qrCodeUrl}
              data-testid="download-qrcode"
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
            <p>
              <strong>How to use:</strong> Print this QR code on flyers, business cards, 
              or displays. When scanned, it will open the chat directly on any smartphone.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
